<template>
  <div class="playground-container">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          Playground
        </h1>
      </div>

      <div class="main-content">
        <div class="content-wrapper">
          <div class="inputs-section">
            <div class="input-group">
              <div class="text-element-2 text-semibold mb-2">
                Voice
              </div>
              <MankSelect
                v-model="selectedVoice"
                :items="voices"
                placeholder="Select Voice"
                variant="outlined"
                density="compact"
                class="voice-select"
                :menu-props="{
                  maxHeight:300,
                  offsetY:true,
                  nudgeBottom:8,
                }"
                item-text="title"
                item-value="value">
                <template v-slot:prepend-inner>
                  <Icon name="volume-up" variant="solid" color="muted"/>
                </template>
                <template v-slot:append>
                  <Icon name="expand-down" variant="solid" color="muted"/>
                </template>
                <template v-slot:append-item>
                  <v-btn
                    color="primary"
                    size="large"
                    class="start-call-btn mank-btn"
                    @click="startCall"
                    elevation="0"
                    text
                    block>
                    <Icon name="plus" variant="solid" color="primary"/>
                    Create Voice
                  </v-btn>
                </template>
              </MankSelect>
            </div>

            <div class="input-group">
              <div class="text-element-2 text-semibold mb-2">
                System Prompt
              </div>
              <div class="text-box">
                <MankTextarea
                  v-model="systemPrompt"
                  placeholder="You are a helpful assistant."
                  variant="outlined"
                  rows="4"
                  class="system-prompt-textarea"/>
              </div>
            </div>

            <v-btn
              color="primary"
              size="large"
              class="start-call-btn mank-btn"
              :disabled="!selectedVoice || !systemPrompt"
              @click="startCall"
              elevation="0"
              block>
              <Icon name="start-call" variant="solid" color="onPrimary"/>
              Start a Call
            </v-btn>
          </div>

          <div class="call-interface">
            <v-card class="call-card" elevation="0">
              <div class="call-header">
                <div class="agent-section">
                  <div class="text-h6 text-bold">
                    Agent
                  </div>
                  <div class="status-indicator" v-if="false">
                    <v-icon size="16">
                      mdi-square
                    </v-icon>
                    <span class="text-caption-1">Waiting</span>
                  </div>
                </div>
                <div class="user-section">
                  <div class="text-h6 text-bold">
                    User
                  </div>
                  <div class="status-indicator" v-if="false">
                    <v-icon size="16">
                      mdi-square
                    </v-icon>
                    <span class="text-caption-1">Waiting</span>
                  </div>
                </div>
              </div>

              <div class="call-content">
                <div class="empty-state text-center" v-if="!isCallActive">
                  <div class="empty-state-icon mb-3">
                    <Icon name="start-call" variant="solid" color="primary"/>
                  </div>
                  <div class="empty-state-text">
                    <h3 class="text-h6 text-bold mb-1">
                      Start a Call
                    </h3>
                    <p class="text-caption-1 text--secondary">
                      Please select a voice and write a system prompt to start a call.
                    </p>
                  </div>
                </div>

                <div class="call-messages" v-else/>
              </div>
            </v-card>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Playground',
  data() {
    return {
      selectedVoice: null,
      systemPrompt: '',
      isCallActive: false,
      voices: [
        { title: 'Alice', value: 'alice' },
        { title: 'Bob', value: 'bob' },
        { title: 'Charlie', value: 'charlie' },
        { title: 'Diana', value: 'diana' },
      ],
    };
  },
  methods: {
    startCall() {
      if (this.selectedVoice && this.systemPrompt) {
        this.isCallActive = true;
        // TODO: Implement actual call start logic
        console.log('Starting call with:', {
          voice: this.selectedVoice,
          prompt: this.systemPrompt,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

.main-content {
  padding: 24px;
}

.content-wrapper {
  display: flex;
  gap: 40px;
  height: 100%;
}

.inputs-section {
  width: 320px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.voice-select {
  :deep(.v-field) {
    background: $light-surface-1;
    border-radius: $card-border-radius;
    border: none;
    box-shadow: none;
  }

  :deep(.v-field__input) {
    font-family: $body-font-family;
    color: $light-foreground-4;
    padding: 6px 8px;
    min-height: 32px;
  }

  :deep(.v-field__prepend-inner) {
    padding-inline-end: 6px;
  }
}

.text-box {
  position: relative;

  .system-prompt-textarea {
    ::v-deep .v-input__slot {
      align-items: stretch;
      border-radius: $card-border-radius;
    }

    ::v-deep fieldset {
      border: none;
    }

    ::v-deep textarea {
      &::placeholder {
        color: $light-foreground-3;
      }
    }
  }

  .text-box-icon {
    position: absolute;
    bottom: 8px;
    right: 8px;
    pointer-events: none;
  }
}

.start-call-btn {
  max-height: 32px;
  font-family: $label-font-family;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;

  &:disabled {
    opacity: 0.6;
  }
}

.call-interface {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.call-card {
  flex: 1;
  background: $light-surface-1;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
}

.call-header {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid $light-border;
  gap: 16px;
  height: 61px;
}

.agent-section,
.user-section {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-section {
  align-items: flex-end;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0; /* Hidden by default as shown in Figma */
}

.call-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.empty-state-icon {
  width: 48px;
  height: 48px;
  background: $light-surface-2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.call-messages {
  width: 100%;
  height: 100%;
  // Future: Messages display area
}

@media (max-width: map-get($grid-breakpoints, md)) {
  .content-wrapper {
    flex-direction: column;
    gap: 24px;
  }

  .inputs-section {
    width: 100%;
  }
}

::v-deep .mank-select {
  border-radius: 8px;
  background: $light-surface-1;
  height: 32px;
  .v-input__slot{
    min-height: 32px;
    padding: 6px 8px !important;
  }
  .v-input__prepend-inner{
    padding: 0;
    margin: auto;
    margin-right: 6px;
  }
  .v-input__append-inner{
    padding: 0;
    margin: auto;
    margin-left: 6px;
  }
  .v-select__selections{
    font-size: $body-3-size;
    font-weight: 600;
    color: $light-foreground-4;
    padding: 0;
    input{
      padding: 0;
    }
  }
}
::v-deep .mank-btn{
  font-size: $body-3-size;
  font-weight: 600;
  .icon-component{
    margin-right: 8px;
  }
}
</style>
